import BaseService from '@/services/BaseService';
import { ResponseError } from '@/lib/exceptions';
import type { ChartDataPoint } from './ChartQueryService';
import type { NonUsResiDashboardFilters } from './utils/buildNonUsResiDashboardFilters';

const NON_US_RESI_DASHBOARD_KPI_PATH = 'non-us-resi-dashboard/kpi';
const NON_US_RESI_DASHBOARD_FILTER_OPTIONS_PATH = 'non-us-resi-dashboard/filter-options';
const NON_US_RESI_DASHBOARD_AS_OF_DATES_PATH = 'non-us-resi-dashboard/as-of-dates';

export type NonUsResiDashboardFilterOptions = {
  dealNames: string[];
};

export type NonUsResiDashboardKpi = {
  activeLoansTotal: number;
  totalBpoBillions: number | null;
  facilitiesWithBpo: number;
  totalFacilities: number;
  waLtvPct: number | null;
  activeFacilities: number;
  maxAsOfMonthLabel: string | null;
};

export type NonUsResiOutstandingLoanCountByDealRow = {
  asOfMonth: string;
  dealName: string;
  loanCount: number;
};

export type NonUsResiBpoCollateralByDealRow = {
  asOfMonth: string;
  dealName: string;
  bpoTotalMillions: number;
};

export type NonUsResiWaLtvByDealRow = {
  asOfMonth: string;
  dealName: string;
  waLtvPct: number;
};

export type NonUsResiPortfolioBuildUpRow = {
  asOfDateIso: string;
  totalLoans: number;
  activeFacilities: number;
  collateralTotalB: number;
};

export type NonUsResiLtvDistributionRow = {
  asOfMonth: string;
  ltvBand: string;
  loanCount: number;
  riskColour: string;
};

export type NonUsResiLoanCountMixRow = {
  asOfMonth: string;
  label: string;
  loanCount: number;
};

export type NonUsResiOriginationVintageRow = {
  originationYear: number;
  loanCount: number;
  pctOfTotal: number;
  facilitiesContributing: number;
};

export type NonUsResiOriginationVintageResult = {
  facilitiesWithStartDate: number;
  totalFacilities: number;
  rows: NonUsResiOriginationVintageRow[];
};

export type NonUsResiWaSeasoningByFacilityRow = {
  dealName: string;
  snapshotDateIso: string | null;
  loansInCalc: number;
  meanSeasoningMonths: number;
  medianSeasoningMonths: number | null;
  minMonths: number;
  maxMonths: number;
  p25Months: number | null;
  p75Months: number | null;
};

const NON_US_RESI_OUTSTANDING_LOAN_COUNT_PATH = 'non-us-resi-dashboard/outstanding-loan-count-by-deal';
const NON_US_RESI_BPO_COLLATERAL_PATH = 'non-us-resi-dashboard/bpo-collateral-by-deal';
const NON_US_RESI_WA_LTV_PATH = 'non-us-resi-dashboard/wa-ltv-by-deal';
const NON_US_RESI_PORTFOLIO_BUILD_UP_PATH = 'non-us-resi-dashboard/portfolio-build-up';
const NON_US_RESI_LTV_DISTRIBUTION_PATH = 'non-us-resi-dashboard/ltv-distribution';
const NON_US_RESI_PROPERTY_TYPE_MIX_PATH = 'non-us-resi-dashboard/property-type-mix';
const NON_US_RESI_LOAN_PURPOSE_MIX_PATH = 'non-us-resi-dashboard/loan-purpose-mix';
const NON_US_RESI_ORIGINATION_VINTAGE_PATH = 'non-us-resi-dashboard/origination-vintage';
const NON_US_RESI_WA_SEASONING_BY_FACILITY_PATH = 'non-us-resi-dashboard/wa-seasoning-by-facility';

function parseNum(v: unknown): number {
  if (v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function parseOptionalNum(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function parseKpiBody(body: Record<string, unknown>): NonUsResiDashboardKpi {
  return {
    activeLoansTotal: parseNum(body.activeLoansTotal ?? body.ActiveLoansTotal),
    totalBpoBillions: parseOptionalNum(body.totalBpoBillions ?? body.TotalBpoBillions),
    facilitiesWithBpo: parseNum(body.facilitiesWithBpo ?? body.FacilitiesWithBpo),
    totalFacilities: parseNum(body.totalFacilities ?? body.TotalFacilities),
    waLtvPct: parseOptionalNum(body.waLtvPct ?? body.WaLtvPct),
    activeFacilities: parseNum(body.activeFacilities ?? body.ActiveFacilities),
    maxAsOfMonthLabel:
      typeof (body.maxAsOfMonthLabel ?? body.MaxAsOfMonthLabel) === 'string'
        ? String(body.maxAsOfMonthLabel ?? body.MaxAsOfMonthLabel)
        : null,
  };
}

function stringArrayFromBody(body: Record<string, unknown>, camelKey: string, pascalKey: string): string[] {
  const raw = body[camelKey] ?? body[pascalKey];
  if (!Array.isArray(raw)) return [];
  return raw.filter((x): x is string => typeof x === 'string');
}

export function appendNonUsResiDashboardFilterParams(qs: URLSearchParams, filters: NonUsResiDashboardFilters): void {
  const asOf = filters.asOfDate.trim();
  if (asOf) qs.set('asOfDate', asOf);
  for (const v of filters.dealNames) {
    const t = v.trim();
    if (t) qs.append('dealName', t);
  }
}

function parseOutstandingLoanCountRows(body: Record<string, unknown>): NonUsResiOutstandingLoanCountByDealRow[] {
  const raw = body.rows ?? body.Rows;
  if (!Array.isArray(raw)) return [];

  const out: NonUsResiOutstandingLoanCountByDealRow[] = [];
  for (const item of raw) {
    if (item == null || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const month = r.asOfMonth ?? r.AsOfMonth;
    const deal = r.dealName ?? r.DealName;
    if (typeof month !== 'string' || month.length === 0) continue;
    if (typeof deal !== 'string' || deal.length === 0) continue;
    out.push({
      asOfMonth: month,
      dealName: deal,
      loanCount: Math.trunc(parseNum(r.loanCount ?? r.LoanCount)),
    });
  }
  return out;
}

/** Long-form points for {@link AutoChart} stacked-bar (xAxis = YYYY-MM, series = deal name). */
export function nonUsResiOutstandingLoanCountRowsToChartDataPoints(
  rows: readonly NonUsResiOutstandingLoanCountByDealRow[],
): ChartDataPoint[] {
  const totals = new Map<string, number>();
  for (const row of rows) {
    const key = `${row.asOfMonth}\u0001${row.dealName}`;
    totals.set(key, (totals.get(key) ?? 0) + row.loanCount);
  }

  const points: ChartDataPoint[] = [];
  for (const [key, value] of totals) {
    const sep = key.indexOf('\u0001');
    points.push({
      xAxis: key.slice(0, sep),
      series: key.slice(sep + 1),
      value,
    });
  }
  return points;
}

function parseBpoCollateralRows(body: Record<string, unknown>): NonUsResiBpoCollateralByDealRow[] {
  const raw = body.rows ?? body.Rows;
  if (!Array.isArray(raw)) return [];

  const out: NonUsResiBpoCollateralByDealRow[] = [];
  for (const item of raw) {
    if (item == null || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const month = r.asOfMonth ?? r.AsOfMonth;
    const deal = r.dealName ?? r.DealName;
    if (typeof month !== 'string' || month.length === 0) continue;
    if (typeof deal !== 'string' || deal.length === 0) continue;
    const bpoMillions = parseOptionalNum(r.bpoTotalMillions ?? r.BpoTotalMillions);
    if (bpoMillions == null) continue;
    out.push({
      asOfMonth: month,
      dealName: deal,
      bpoTotalMillions: bpoMillions,
    });
  }
  return out;
}

/**
 * Long-form stacked-bar points (xAxis = YYYY-MM, series = deal).
 * Values are scaled to USD so {@link AutoChart} currency axis formatters apply.
 */
export function nonUsResiBpoCollateralRowsToChartDataPoints(
  rows: readonly NonUsResiBpoCollateralByDealRow[],
): ChartDataPoint[] {
  const totals = new Map<string, number>();
  for (const row of rows) {
    const key = `${row.asOfMonth}\u0001${row.dealName}`;
    totals.set(key, (totals.get(key) ?? 0) + row.bpoTotalMillions);
  }

  const points: ChartDataPoint[] = [];
  for (const [key, millions] of totals) {
    const sep = key.indexOf('\u0001');
    points.push({
      xAxis: key.slice(0, sep),
      series: key.slice(sep + 1),
      value: millions * 1_000_000,
    });
  }
  return points;
}

function parseWaLtvRows(body: Record<string, unknown>): NonUsResiWaLtvByDealRow[] {
  const raw = body.rows ?? body.Rows;
  if (!Array.isArray(raw)) return [];

  const out: NonUsResiWaLtvByDealRow[] = [];
  for (const item of raw) {
    if (item == null || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const month = r.asOfMonth ?? r.AsOfMonth;
    const deal = r.dealName ?? r.DealName;
    if (typeof month !== 'string' || month.length === 0) continue;
    if (typeof deal !== 'string' || deal.length === 0) continue;
    const waLtvPct = parseOptionalNum(r.waLtvPct ?? r.WaLtvPct);
    if (waLtvPct == null) continue;
    out.push({
      asOfMonth: month,
      dealName: deal,
      waLtvPct,
    });
  }
  return out;
}

/**
 * Long-form line chart points (xAxis = YYYY-MM, series = deal).
 * Values are 0–1 ratios for {@link AutoChart} percent formatters (Auto WA APR style).
 */
export function nonUsResiWaLtvRowsToChartDataPoints(rows: readonly NonUsResiWaLtvByDealRow[]): ChartDataPoint[] {
  return rows.map(row => ({
    xAxis: row.asOfMonth,
    series: row.dealName,
    value: row.waLtvPct / 100,
  }));
}

function parsePortfolioBuildUpRows(body: Record<string, unknown>): NonUsResiPortfolioBuildUpRow[] {
  const raw = body.rows ?? body.Rows;
  if (!Array.isArray(raw)) return [];

  const out: NonUsResiPortfolioBuildUpRow[] = [];
  for (const item of raw) {
    if (item == null || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const asOfDateIso = r.asOfDateIso ?? r.AsOfDateIso;
    if (typeof asOfDateIso !== 'string' || asOfDateIso.length === 0) continue;
    const collateralTotalB = parseOptionalNum(r.collateralTotalB ?? r.CollateralTotalB);
    out.push({
      asOfDateIso,
      totalLoans: Math.trunc(parseNum(r.totalLoans ?? r.TotalLoans)),
      activeFacilities: Math.trunc(parseNum(r.activeFacilities ?? r.ActiveFacilities)),
      collateralTotalB: collateralTotalB ?? 0,
    });
  }
  return out;
}

function parseLtvDistributionRows(body: Record<string, unknown>): NonUsResiLtvDistributionRow[] {
  const raw = body.rows ?? body.Rows;
  if (!Array.isArray(raw)) return [];

  const out: NonUsResiLtvDistributionRow[] = [];
  for (const item of raw) {
    if (item == null || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const month = r.asOfMonth ?? r.AsOfMonth;
    const ltvBand = r.ltvBand ?? r.LtvBand;
    if (typeof month !== 'string' || month.length === 0) continue;
    if (typeof ltvBand !== 'string' || ltvBand.length === 0) continue;
    const riskColour = r.riskColour ?? r.RiskColour;
    out.push({
      asOfMonth: month,
      ltvBand,
      loanCount: Math.trunc(parseNum(r.loanCount ?? r.LoanCount)),
      riskColour: typeof riskColour === 'string' && riskColour.length > 0 ? riskColour : 'teal',
    });
  }
  return out;
}

/** Portfolio share (0–1) per month for {@link AutoChart} <c>stacked-bar-share</c>. */
export function nonUsResiLtvDistributionRowsToShareChartDataPoints(
  rows: readonly NonUsResiLtvDistributionRow[],
): ChartDataPoint[] {
  const counts = new Map<string, number>();
  const monthTotals = new Map<string, number>();
  for (const row of rows) {
    const key = `${row.asOfMonth}\u0001${row.ltvBand}`;
    counts.set(key, (counts.get(key) ?? 0) + row.loanCount);
    monthTotals.set(row.asOfMonth, (monthTotals.get(row.asOfMonth) ?? 0) + row.loanCount);
  }

  const points: ChartDataPoint[] = [];
  for (const [key, count] of counts) {
    const sep = key.indexOf('\u0001');
    const month = key.slice(0, sep);
    const total = monthTotals.get(month) ?? 0;
    points.push({
      xAxis: month,
      series: key.slice(sep + 1),
      value: total > 0 ? count / total : 0,
    });
  }
  return points;
}

function parseLoanCountMixRows(body: Record<string, unknown>): NonUsResiLoanCountMixRow[] {
  const raw = body.rows ?? body.Rows;
  if (!Array.isArray(raw)) return [];

  const out: NonUsResiLoanCountMixRow[] = [];
  for (const item of raw) {
    if (item == null || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const month = r.asOfMonth ?? r.AsOfMonth;
    const label = r.label ?? r.Label;
    if (typeof month !== 'string' || month.length === 0) continue;
    if (typeof label !== 'string' || label.length === 0) continue;
    out.push({
      asOfMonth: month,
      label,
      loanCount: Math.trunc(parseNum(r.loanCount ?? r.LoanCount)),
    });
  }
  return out;
}

/** Long-form points for {@link AutoChart} stacked-bar (xAxis = YYYY-MM, series = mix label). */
export function nonUsResiLoanCountMixRowsToChartDataPoints(
  rows: readonly NonUsResiLoanCountMixRow[],
): ChartDataPoint[] {
  const totals = new Map<string, number>();
  for (const row of rows) {
    const key = `${row.asOfMonth}\u0001${row.label}`;
    totals.set(key, (totals.get(key) ?? 0) + row.loanCount);
  }

  const points: ChartDataPoint[] = [];
  for (const [key, value] of totals) {
    const sep = key.indexOf('\u0001');
    points.push({
      xAxis: key.slice(0, sep),
      series: key.slice(sep + 1),
      value,
    });
  }
  return points;
}

/** Portfolio share (0–1) per month for {@link AutoChart} <c>stacked-bar-share</c>. */
export function nonUsResiLoanCountMixRowsToShareChartDataPoints(
  rows: readonly NonUsResiLoanCountMixRow[],
): ChartDataPoint[] {
  const counts = new Map<string, number>();
  const monthTotals = new Map<string, number>();
  for (const row of rows) {
    const key = `${row.asOfMonth}\u0001${row.label}`;
    counts.set(key, (counts.get(key) ?? 0) + row.loanCount);
    monthTotals.set(row.asOfMonth, (monthTotals.get(row.asOfMonth) ?? 0) + row.loanCount);
  }

  const points: ChartDataPoint[] = [];
  for (const [key, count] of counts) {
    const sep = key.indexOf('\u0001');
    const month = key.slice(0, sep);
    const total = monthTotals.get(month) ?? 0;
    points.push({
      xAxis: month,
      series: key.slice(sep + 1),
      value: total > 0 ? count / total : 0,
    });
  }
  return points;
}

function parseOriginationVintageResult(body: Record<string, unknown>): NonUsResiOriginationVintageResult {
  const raw = body.rows ?? body.Rows;
  const rows: NonUsResiOriginationVintageRow[] = [];
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (item == null || typeof item !== 'object') continue;
      const r = item as Record<string, unknown>;
      const year = r.originationYear ?? r.OriginationYear;
      if (year == null) continue;
      rows.push({
        originationYear: Math.trunc(parseNum(year)),
        loanCount: Math.trunc(parseNum(r.loanCount ?? r.LoanCount)),
        pctOfTotal: parseOptionalNum(r.pctOfTotal ?? r.PctOfTotal) ?? 0,
        facilitiesContributing: Math.trunc(parseNum(r.facilitiesContributing ?? r.FacilitiesContributing)),
      });
    }
  }

  return {
    facilitiesWithStartDate: Math.trunc(parseNum(body.facilitiesWithStartDate ?? body.FacilitiesWithStartDate)),
    totalFacilities: Math.trunc(parseNum(body.totalFacilities ?? body.TotalFacilities)),
    rows,
  };
}

function parseWaSeasoningByFacilityRows(body: Record<string, unknown>): NonUsResiWaSeasoningByFacilityRow[] {
  const raw = body.rows ?? body.Rows;
  if (!Array.isArray(raw)) return [];

  const out: NonUsResiWaSeasoningByFacilityRow[] = [];
  for (const item of raw) {
    if (item == null || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const dealName = r.dealName ?? r.DealName;
    if (typeof dealName !== 'string' || dealName.length === 0) continue;

    const snapshotRaw = r.snapshotDateIso ?? r.SnapshotDateIso;
    out.push({
      dealName,
      snapshotDateIso: typeof snapshotRaw === 'string' && snapshotRaw.length > 0 ? snapshotRaw : null,
      loansInCalc: Math.trunc(parseNum(r.loansInCalc ?? r.LoansInCalc)),
      meanSeasoningMonths: parseOptionalNum(r.meanSeasoningMonths ?? r.MeanSeasoningMonths) ?? 0,
      medianSeasoningMonths: parseOptionalNum(r.medianSeasoningMonths ?? r.MedianSeasoningMonths),
      minMonths: Math.trunc(parseNum(r.minMonths ?? r.MinMonths)),
      maxMonths: Math.trunc(parseNum(r.maxMonths ?? r.MaxMonths)),
      p25Months: parseOptionalNum(r.p25Months ?? r.P25Months),
      p75Months: parseOptionalNum(r.p75Months ?? r.P75Months),
    });
  }
  return out;
}

export function formatNonUsResiLoanCount(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }
  return Math.round(value).toLocaleString('en-US');
}

/** <c>total_bpo_billions</c> from API is already in billions USD. */
export function formatNonUsResiBpoBillions(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }
  const billions = Number(value);
  const dollarsFormatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(billions);
  return `${dollarsFormatted}B`;
}

export function formatNonUsResiBpoCoverageSubtitle(facilitiesWithBpo: number, totalFacilities: number): string {
  const withBpo = Math.round(facilitiesWithBpo).toLocaleString('en-US');
  const total = Math.round(totalFacilities).toLocaleString('en-US');
  return `${withBpo} out of ${total} facilities report $ value`;
}

export function formatNonUsResiStartDateCoverageSubtitle(
  facilitiesWithStartDate: number,
  totalFacilities: number,
): string {
  const withStart = Math.round(facilitiesWithStartDate).toLocaleString('en-US');
  const total = Math.round(totalFacilities).toLocaleString('en-US');
  return `${withStart} of ${total} facilities w/ start date`;
}

/** BPO-weighted average LTV percent from API (<c>wa_ltv_pct</c>). */
export function formatNonUsResiWaLtvPct(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }
  return `${Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

export const NON_US_RESI_WA_LTV_SUBTITLE = 'Ex-Lannock facilities (BPO-weighted)';

export const NON_US_RESI_WA_SEASONING_SUBTITLE = 'Mean loan age in months';

export function formatNonUsResiActiveFacilitiesSubtitle(maxAsOfMonthLabel: string | null | undefined): string {
  const month = maxAsOfMonthLabel?.trim();
  if (month) {
    return `As of ${month}`;
  }
  return 'As of max month in the dataset across all deals';
}

export class NonUsResiDashboardService extends BaseService {
  constructor() {
    super('/api/digitalconnect-service');
  }

  getJsonBody = async (path: string) => {
    const raw = await this.get({ path: path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      return (await raw.json()) as Record<string, unknown>;
    }
    if (raw && typeof raw === 'object') return raw as Record<string, unknown>;
    return {};
  };

  fetchNonUsResiDashboardFilterOptions = async () => {
    const body = await this.getJsonBody(NON_US_RESI_DASHBOARD_FILTER_OPTIONS_PATH);
    return {
      dealNames: stringArrayFromBody(body, 'dealNames', 'DealNames'),
    };
  };

  fetchNonUsResiDashboardAsOfDates = async () => {
    const body = await this.getJsonBody(NON_US_RESI_DASHBOARD_AS_OF_DATES_PATH);
    return stringArrayFromBody(body, 'asOfDates', 'AsOfDates');
  };

  fetchNonUsResiDashboardKpi = async (filters: NonUsResiDashboardFilters = { dealNames: [], asOfDate: '' }) => {
    const qs = new URLSearchParams();
    appendNonUsResiDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path = q.length > 0 ? `${NON_US_RESI_DASHBOARD_KPI_PATH}?${q}` : NON_US_RESI_DASHBOARD_KPI_PATH;

    const raw = await this.get({ path: path });

    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const body = (await raw.json()) as Record<string, unknown>;
      return parseKpiBody(body);
    }

    return parseKpiBody((raw ?? {}) as Record<string, unknown>);
  };

  fetchNonUsResiOutstandingLoanCountByDeal = async (
    filters: NonUsResiDashboardFilters = { dealNames: [], asOfDate: '' },
  ) => {
    const qs = new URLSearchParams();
    appendNonUsResiDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path =
      q.length > 0 ? `${NON_US_RESI_OUTSTANDING_LOAN_COUNT_PATH}?${q}` : NON_US_RESI_OUTSTANDING_LOAN_COUNT_PATH;

    const body = await this.getJsonBody(path);
    return parseOutstandingLoanCountRows(body);
  };

  fetchNonUsResiBpoCollateralByDeal = async (filters: NonUsResiDashboardFilters = { dealNames: [], asOfDate: '' }) => {
    const qs = new URLSearchParams();
    appendNonUsResiDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path = q.length > 0 ? `${NON_US_RESI_BPO_COLLATERAL_PATH}?${q}` : NON_US_RESI_BPO_COLLATERAL_PATH;

    const body = await this.getJsonBody(path);
    return parseBpoCollateralRows(body);
  };

  fetchNonUsResiWaLtvByDeal = async (filters: NonUsResiDashboardFilters = { dealNames: [], asOfDate: '' }) => {
    const qs = new URLSearchParams();
    appendNonUsResiDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path = q.length > 0 ? `${NON_US_RESI_WA_LTV_PATH}?${q}` : NON_US_RESI_WA_LTV_PATH;

    const body = await this.getJsonBody(path);
    return parseWaLtvRows(body);
  };

  fetchNonUsResiPortfolioBuildUp = async (filters: NonUsResiDashboardFilters = { dealNames: [], asOfDate: '' }) => {
    const qs = new URLSearchParams();
    appendNonUsResiDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path = q.length > 0 ? `${NON_US_RESI_PORTFOLIO_BUILD_UP_PATH}?${q}` : NON_US_RESI_PORTFOLIO_BUILD_UP_PATH;

    const body = await this.getJsonBody(path);
    return parsePortfolioBuildUpRows(body);
  };

  fetchNonUsResiLtvDistribution = async (filters: NonUsResiDashboardFilters = { dealNames: [], asOfDate: '' }) => {
    const qs = new URLSearchParams();
    appendNonUsResiDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path = q.length > 0 ? `${NON_US_RESI_LTV_DISTRIBUTION_PATH}?${q}` : NON_US_RESI_LTV_DISTRIBUTION_PATH;

    const body = await this.getJsonBody(path);
    return parseLtvDistributionRows(body);
  };

  fetchNonUsResiLoanCountMix = async (
    pathBase: string,
    filters: NonUsResiDashboardFilters,
  ): Promise<NonUsResiLoanCountMixRow[]> => {
    const qs = new URLSearchParams();
    appendNonUsResiDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path = q.length > 0 ? `${pathBase}?${q}` : pathBase;
    const body = await this.getJsonBody(path);
    return parseLoanCountMixRows(body);
  };

  fetchNonUsResiPropertyTypeMix = (filters: NonUsResiDashboardFilters = { dealNames: [], asOfDate: '' }) => {
    return this.fetchNonUsResiLoanCountMix(NON_US_RESI_PROPERTY_TYPE_MIX_PATH, filters);
  };

  fetchNonUsResiLoanPurposeMix = (filters: NonUsResiDashboardFilters = { dealNames: [], asOfDate: '' }) => {
    return this.fetchNonUsResiLoanCountMix(NON_US_RESI_LOAN_PURPOSE_MIX_PATH, filters);
  };

  fetchNonUsResiOriginationVintage = async (filters: NonUsResiDashboardFilters = { dealNames: [], asOfDate: '' }) => {
    const qs = new URLSearchParams();
    appendNonUsResiDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path = q.length > 0 ? `${NON_US_RESI_ORIGINATION_VINTAGE_PATH}?${q}` : NON_US_RESI_ORIGINATION_VINTAGE_PATH;

    const body = await this.getJsonBody(path);
    return parseOriginationVintageResult(body);
  };

  fetchNonUsResiWaSeasoningByFacility = async (
    filters: NonUsResiDashboardFilters = { dealNames: [], asOfDate: '' },
  ) => {
    const qs = new URLSearchParams();
    appendNonUsResiDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path =
      q.length > 0 ? `${NON_US_RESI_WA_SEASONING_BY_FACILITY_PATH}?${q}` : NON_US_RESI_WA_SEASONING_BY_FACILITY_PATH;

    const body = await this.getJsonBody(path);
    return parseWaSeasoningByFacilityRows(body);
  };
}
